import LoggingBase from '../../../../base/loggingbase';
import Validator from '../../../../utils/Validator';

/**
 * class AutoFitModeExtension - holds the "auto-fit mode" column computations for the host
 * table ({@link XtwTbl}). It is a plain delegate: the host is passed in and accessed via
 * {@link #host}. Owned by {@link AutoFitModeManager}.
 */
export default class AutoFitModeExtension extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} host the host table (XtwTbl)
	 * @param {Boolean} isAutoFitModeOn whether the auto-fit mode is enabled
	 */
	constructor( host, isAutoFitModeOn = false ) {
		super( 'widgets.xtw.autofit.AutoFitModeExtension' );
		this._host = host;
		// the "auto-fit mode" is "final"; in the future this might change
		Object.defineProperty( this, "isAutoFitModeOn", {
			value: !!isAutoFitModeOn,
			writable: false,
			configurable: false
		} );
	}

	/**
	 * @returns {*} the host table
	 */
	get host() {
		return this._host;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._host = void 0;
		super.doDestroy();
	}

	get shouldAutoFit() {
		const host = this.host;
		if ( !this.isAutoFitModeOn || !Validator.isObject( host ) ||
			host.isRowTpl || !host.ready || !host.alive ) {
			return false;
		}
		return true;
	}

	get orderedColumns() {
		const host = this.host;
		if ( !Validator.isObject( host ) ) {
			return void 0;
		}
		return Validator.isObject( host.wdgHead ) &&
			"orderedColumns" in host.wdgHead ?
			host.wdgHead.orderedColumns : void 0;
	}

	get visibleColumns() {
		const orderedColumns = this.orderedColumns;
		if ( !Validator.isArray( orderedColumns, true ) ) {
			return void 0;
		}
		const visibleColumns = [ ...orderedColumns ].filter( column =>
			Validator.isObject( column ) && column.visible &&
			Validator.isPositiveNumber( column.defWidth, false ) );
		return visibleColumns.length < 1 ? void 0 : visibleColumns;
	}

	get columnsAutoFitProperties() {
		const visibleColumns = this.visibleColumns;
		if ( !Validator.isArray( visibleColumns, true ) ) {
			return void 0;
		}
		const columnsAutoFitProperties = new Map();
		let theoreticalWidthTotal = 0;
		for ( let column of visibleColumns ) {
			const properties = Object.defineProperty( {}, "definedWidth", {
				value: column.defWidth,
				configurable: false,
				writable: false
			} );
			theoreticalWidthTotal += column.defWidth;
			columnsAutoFitProperties.set( column.id, properties );
		}
		for ( let columnId of [ ...columnsAutoFitProperties.keys() ] ) {
			const properties = columnsAutoFitProperties.get( columnId );
			Object.defineProperties( properties, {
				quota: {
					value: properties.definedWidth / theoreticalWidthTotal,
					configurable: false,
					writable: false
				},
				// TODO should this part also be defined, or is it excessive/redundant?
				theoreticalWidthTotal: {
					value: theoreticalWidthTotal,
					configurable: false,
					writable: false
				}
			} );
			columnsAutoFitProperties.set( columnId, properties );
		}
		return columnsAutoFitProperties;
	}

	get selectionColumn() {
		const host = this.host;
		if ( !Validator.isObject( host ) ) {
			return void 0;
		}
		if ( !Validator.isObject( host.wdgHead ) ||
			!Validator.is( host.wdgHead.columns, "ObjReg" ) ) {
			return void 0;
		}
		let selectionColumn = host.wdgHead.columns.getItem( "o-1" );
		if ( Validator.is( selectionColumn, "XtwCol" ) ) {
			return selectionColumn;
		}
		selectionColumn = host.wdgHead.columns.getObj( "o-1" );
		if ( Validator.is( selectionColumn, "XtwCol" ) ) {
			return selectionColumn;
		}
		selectionColumn = host.wdgHead.columns.getFirst();
		return Validator.is( selectionColumn, "XtwCol" ) ?
			selectionColumn : void 0;
	}

	get selectionColumnWidth() {
		const selectionColumn = this.selectionColumn;
		if ( !Validator.isObject( selectionColumn ) ) {
			return void 0;
		}
		return Validator.isPositiveNumber( selectionColumn.width ) ?
			selectionColumn.width : void 0;
	}

	get verticalSliderWidth() {
		const host = this.host;
		if ( !Validator.isObject( host ) ) {
			return void 0;
		}
		if ( !Validator.isObject( host.wdgSlv ) ) {
			return host.verticalSliderOriginalWidth;
		}
		const widthProperty = [ "_computedWidthValue", "_cachedBoxWidth",
			"_cachedInnerWidth", "__user$width"
		].find( property =>
			Validator.isPositiveNumber( host.wdgSlv[ property ], false ) );
		return Validator.isString( widthProperty ) ?
			host.wdgSlv[ widthProperty ] : void 0;
	}

	getNeighbourColumn( column, toTheRight = true ) {
		if ( !Validator.isObject( column ) ) {
			return void 0;
		}
		const visibleColumns = this.visibleColumns;
		if ( !Validator.isArray( visibleColumns, true ) ) {
			return void 0;
		}
		if ( visibleColumns.length == 1 ) {
			return void 0;
		}
		const columnOrderIndex = Validator.getIndexInArray( visibleColumns, column );
		if ( !Validator.isPositiveInteger( columnOrderIndex ) ) {
			return void 0;
		}
		const neighbourColumnIndex = columnOrderIndex == 0 ? 1 :
			columnOrderIndex == visibleColumns.length - 1 ? visibleColumns.length - 2 :
			!!toTheRight ? columnOrderIndex + 1 : columnOrderIndex - 1;
		const neighbourColumn = visibleColumns[ neighbourColumnIndex ];
		return Validator.isObject( neighbourColumn ) ? neighbourColumn : void 0;
	}

}
